/**
 * Shared network ignore lists for different monitoring purposes
 *
 * Used by:
 * - TTI tracking (navigationTTI.ts, interactionTTI.ts)
 * - Soft Navigation tracking (softNavigationHelper.ts)
 * - Resource monitoring (resourceMonitor.ts)
 */

// ============================================================================
// TTI Allowlist
// ============================================================================
// Only capture network requests from these domains for TTI/Soft Navigation tracking
// This filters out third-party scripts (analytics, widgets, etc.) that don't affect
// the actual page content loading.

export const ALLOW_FOR_TTI = [
  "staging.gohighlevel.com", // Staging domain
  "leadconnectorhq.com", // Primary API domain
  "localhost", // Local development
  "gohighlevel.site", // Versioned development domain
];

// ============================================================================
// TTI Path Ignore List
// ============================================================================
// Patterns to ignore in URL paths even for allowed domains
// These are typically monitoring endpoints or versioned API endpoints we don't want to track

export const IGNORE_PATHS_FOR_TTI = [
  "performance", // Our own monitoring endpoints
  "/1/", // Versioned API endpoints
];

/**
 * Check if URL should be ignored for TTI tracking
 *
 * Uses allowlist approach: Only captures requests from leadconnector.com domains.
 * This filters out all third-party requests (analytics, widgets, external media)
 * that don't actually affect page content loading.
 *
 * @returns true if the URL should be IGNORED (not tracked)
 */
export const shouldIgnoreForTTI = (url: string): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);

    // First check: Is this from an allowed domain?
    const isAllowedDomain = ALLOW_FOR_TTI.some((domain) =>
      urlObj.hostname.includes(domain)
    );

    // If not from allowed domain, ignore it
    if (!isAllowedDomain) {
      return true;
    }

    // Check if path contains any ignored patterns
    const pathname = urlObj.pathname.toLowerCase();
    const isIgnoredPath = IGNORE_PATHS_FOR_TTI.some((pattern) =>
      pathname.includes(pattern.toLowerCase())
    );

    if (isIgnoredPath) {
      return true;
    }

    return false;
  } catch {
    // If URL parsing fails, ignore it
    return true;
  }
};

// ============================================================================
// Resource Monitoring Ignore List
// ============================================================================
// URLs to skip for resource monitoring - avoid recursive logging of our own endpoints

export const IGNORE_FOR_RESOURCE_MONITORING = [
  "resource-performance-logs",
  "page-load-performance-logs",
  "promise-executor-logs",
  "firestore.googleapis.com",
];

/**
 * Check if URL should be ignored for resource monitoring
 */
export const shouldIgnoreForResourceMonitoring = (url: string): boolean => {
  return IGNORE_FOR_RESOURCE_MONITORING.some((pattern) =>
    url.includes(pattern)
  );
};
